import { useState, useRef, useCallback } from 'react';
import JSZip from 'jszip';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';

// "2004198-ACCOUNT-32825548.zip" or "2004198-FILES/" → "2004198"
function groupKeyFromName(name) {
  const m = name.match(/^(\d+)-/);
  return m ? m[1] : null;
}

// Find group key by scanning each path segment for a leading-number pattern
function groupKeyFromPath(path) {
  for (const seg of path.split('/')) {
    const key = groupKeyFromName(seg);
    if (key) return key;
  }
  return null;
}

// Try to find an iCloud email address anywhere in a file path
function extractEmail(path) {
  for (const part of path.split('/')) {
    if (part.includes('@')) {
      return part.replace(/-\d+$/, ''); // "niagray43@icloud.com-2004198" → "niagray43@icloud.com"
    }
  }
  return null;
}

// Return true for paths that are macOS junk or should be ignored
function isJunk(path) {
  if (path.includes('__MACOSX/')) return true;
  const base = path.split('/').pop();
  if (base === '.DS_Store') return true;
  if (base.startsWith('._')) return true;
  if (path.toLowerCase().endsWith('.gpg')) return true;
  return false;
}

// Recursively extract all files from a JSZip object into groupObj.data,
// prefixing each file path with pathPrefix.
// If any entry inside is itself a .zip, it is extracted recursively too.
async function extractAllFiles(zip, pathPrefix, groupObj, setStatusMsg) {
  const entries = [];
  zip.forEach((p, e) => entries.push({ p, e }));

  for (const { p, e } of entries) {
    if (e.dir || isJunk(p)) continue;

    const fullPath = pathPrefix ? `${pathPrefix}/${p}` : p;

    // Discover email from path
    if (!groupObj.email) {
      const email = extractEmail(p);
      if (email) groupObj.email = email;
    }

    if (p.toLowerCase().endsWith('.zip')) {
      // Recursively extract this nested zip
      const buf = await e.async('arraybuffer');
      const baseName = p.replace(/\.zip$/i, '');
      const nestedPrefix = pathPrefix ? `${pathPrefix}/${baseName}` : baseName;
      setStatusMsg(`Extracting ${p.split('/').pop()}…`);
      try {
        const nestedZip = await JSZip.loadAsync(buf);
        await extractAllFiles(nestedZip, nestedPrefix, groupObj, setStatusMsg);
      } catch {
        console.warn('Could not parse nested zip:', p);
      }
    } else {
      const buf = await e.async('arraybuffer');
      // Use only the filename — strip all intermediate folder structure
      const filename = p.split('/').pop();
      groupObj.data.push({ path: filename, buffer: buf });
    }
  }
}

export default function AppleLegalProcessor() {
  const [phase, setPhase] = useState('upload'); // 'upload' | 'processing' | 'results'
  const [groups, setGroups] = useState(null);
  // groups: { "2004198": { email: string|null, sources: string[], fileCount: number,
  //                         data: [{path: string, buffer: ArrayBuffer}] } }
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(null); // groupKey or 'all'
  const fileInputRef = useRef(null);

  const processFile = useCallback(async (file) => {
    trackToolAction('/applelegal', 'Apple Legal Process Organizer');
    setPhase('processing');
    setError('');
    setStatusMsg('Opening archive…');

    try {
      const mainZip = await JSZip.loadAsync(file);

      const outerEntries = [];
      mainZip.forEach((path, entry) => outerEntries.push({ path, entry }));

      const result = {};
      // result[key] = { email: null, sources: Set<string>, data: [{path, buffer}] }

      for (const { path, entry } of outerEntries) {
        if (entry.dir || isJunk(path)) continue;

        const filename = path.split('/').pop();

        // Determine which account group this entry belongs to.
        // First try the filename itself, then walk up the path segments.
        const key = groupKeyFromName(filename) || groupKeyFromPath(path);
        if (!key) continue;

        if (!result[key]) result[key] = { email: null, sources: new Set(), data: [] };

        if (filename.toLowerCase().endsWith('.zip')) {
          // It's a numbered zip — extract its contents (recursively handles deeper nesting)
          result[key].sources.add(filename);
          setStatusMsg(`Extracting ${filename}…`);

          const buffer = await entry.async('arraybuffer');
          try {
            const innerZip = await JSZip.loadAsync(buffer);
            // Pass null prefix — each inner zip already has its own root folder structure
            await extractAllFiles(innerZip, null, result[key], setStatusMsg);
          } catch {
            console.warn('Could not parse zip:', filename);
          }
        } else {
          // Regular file sitting directly in the outer archive — include as-is
          result[key].sources.add(path.split('/').slice(0, -1).join('/') || filename);
          const buf = await entry.async('arraybuffer');
          result[key].data.push({ path, buffer: buf });
          if (!result[key].email) {
            const email = extractEmail(path);
            if (email) result[key].email = email;
          }
        }
      }

      if (Object.keys(result).length === 0) {
        throw new Error('No numbered account ZIP files were found inside the archive.');
      }

      // Convert sources Set → Array
      for (const g of Object.values(result)) {
        g.sources = [...g.sources];
      }

      setGroups(result);
      setPhase('results');
    } catch (e) {
      setError(e.message || 'Failed to process archive.');
      setPhase('upload');
    }
  }, []);

  const downloadGroup = useCallback(async (key) => {
    setDownloading(key);
    try {
      const group = groups[key];
      const name = group.email ? group.email : `Account_${key}`;
      const zip = new JSZip();
      for (const { path, buffer } of group.data) {
        zip.file(path, buffer);
      }
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  }, [groups]);

  const downloadAll = useCallback(async () => {
    setDownloading('all');
    try {
      const zip = new JSZip();
      for (const [key, group] of Object.entries(groups)) {
        const name = group.email ? group.email : `Account_${key}`;
        const folder = zip.folder(name);
        for (const { path, buffer } of group.data) {
          folder.file(path, buffer);
        }
      }
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Organized_Accounts.zip';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  }, [groups]);

  const reset = () => { setPhase('upload'); setGroups(null); setError(''); };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  // ── UPLOAD / PROCESSING ────────────────────────────────────────────────
  if (phase === 'upload' || phase === 'processing') {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <Navbar pageTitle="Apple Legal Process Organizer" />
        <div style={{ maxWidth: 580, margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px', margin: 0 }}>
              Apple Legal Process Organizer
            </h2>
            <p style={{ color: '#64748b', marginTop: 8, fontSize: 15, lineHeight: 1.6 }}>
              Upload a ZIP containing all files from the Apple BASH script used to download the data
              return. The tool groups nested ZIP files by account number and packages them into
              organized folders for download.
            </p>
          </div>

          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
              padding: '12px 16px', marginBottom: 20, color: '#b91c1c', fontSize: 14,
            }}>
              {error}
            </div>
          )}

          {phase === 'processing' ? (
            <div style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
              padding: '40px 32px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 32, marginBottom: 14 }}>⚙️</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 8 }}>
                Processing archive…
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{statusMsg}</div>
            </div>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed #cbd5e1', borderRadius: 12,
                padding: '56px 32px', textAlign: 'center',
                cursor: 'pointer', background: '#fff',
                transition: 'border-color .2s, background .2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#fff'; }}
            >
              <div style={{ fontSize: 40, marginBottom: 14 }}>📦</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 6 }}>
                Drop Archive.zip here
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8' }}>or click to browse</div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) processFile(e.target.files[0]); }}
              />
            </div>
          )}

          <div style={{ marginTop: 20, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12, color: '#15803d' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Files are processed entirely in your browser — nothing is ever uploaded to any server.
          </div>
        </div>
      </div>
    );
  }

  // ── RESULTS ────────────────────────────────────────────────────────────
  const groupEntries = Object.entries(groups).sort(([a], [b]) =>
    a.localeCompare(b, undefined, { numeric: true })
  );
  const totalFiles = groupEntries.reduce((sum, [, g]) => sum + g.data.length, 0);

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <Navbar pageTitle="Apple Legal Process Organizer" />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px 80px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 28 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>
              {groupEntries.length} Account Group{groupEntries.length !== 1 ? 's' : ''} Found
            </h2>
            <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
              {totalFiles} file{totalFiles !== 1 ? 's' : ''} total · {groupEntries.length} account{groupEntries.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={downloadAll}
              disabled={!!downloading}
              style={{
                background: downloading === 'all' ? '#818cf8' : '#4f46e5',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '9px 18px', fontWeight: 700, fontSize: 13,
                cursor: downloading ? 'wait' : 'pointer',
              }}
            >
              {downloading === 'all' ? '⏳ Packaging…' : '⬇ Download All Organized'}
            </button>
            <button
              onClick={reset}
              style={{
                background: '#fff', color: '#64748b', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: '9px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}
            >
              New File
            </button>
          </div>
        </div>

        {/* Account cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {groupEntries.map(([key, group]) => (
            <div
              key={key}
              style={{
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
                padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Account header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                      background: '#ccfbf1',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20,
                    }}>
                      🍎
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>
                        {group.email || `Account #${key}`}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                        Request #{key}
                      </div>
                    </div>
                  </div>

                  {/* Source ZIP list */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {group.sources.map(src => (
                      <div key={src} style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14 }}>📦</span>
                        <span style={{ fontFamily: 'monospace' }}>{src}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 10, fontSize: 12, color: '#94a3b8' }}>
                    {group.data.length} file{group.data.length !== 1 ? 's' : ''}
                    {group.data.length === 0 && ' — no extractable content (may be empty or all metadata)'}
                  </div>
                </div>

                {/* Download button */}
                <button
                  onClick={() => downloadGroup(key)}
                  disabled={!!downloading}
                  style={{
                    background: downloading === key ? '#f1f5f9' : '#f8fafc',
                    color: '#0f172a', border: '1px solid #e2e8f0',
                    borderRadius: 8, padding: '9px 16px',
                    fontWeight: 700, fontSize: 13,
                    cursor: downloading ? 'wait' : 'pointer',
                    whiteSpace: 'nowrap', flexShrink: 0,
                    transition: 'background .15s',
                  }}
                >
                  {downloading === key ? '⏳ Packaging…' : `⬇ Download ${group.email || `#${key}`}`}
                </button>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
